import React from "react";
import PropTypes from "prop-types";
import { format } from "date-fns/esm";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import clsx from "clsx";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Config
import colors from "../../config/colors";
import { getRoute, urls } from "../../config/routes";

// Hooks
import useTranslate from "../../hooks/useTranslate";

// Components
import Avatar from "../Avatar";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    flex: "1 1",
    display: "flex"
  },
  userData: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3)
  },
  userDataText: {
    fontFamily: ["Open Sans", "sans-serif"].join(","),
    fontSize: 14,
    color: colors.santasGray,
    marginLeft: theme.spacing(1),
    letterSpacing: 0.5
  },
  authorLink: {
    color: colors.dodgerBlue,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline"
    }
  },
  declaration: {
    whiteSpace: "pre-wrap",
    display: "block",
    lineHeight: 1.5,
    wordBreak: "break-word"
  },
  avatar: {
    width: "1.2em",
    height: "1.2em",
    fontSize: 14
  },
  box: {
    borderBottom: ({ noBorder }) =>
      !noBorder && "1px solid rgba(69, 79, 99, 0.15)",
    paddingBottom: theme.spacing(3),
    minWidth: "100%"
  },
  subTitle: {
    marginBottom: 12
  }
}));

const UserAddition = ({
  author = {},
  className,
  date,
  hideUserInfo = false,
  subTitle,
  text,
  noBorder,
  ...props
}) => {
  const classes = useStyles({ noBorder });
  const getText = useTranslate();
  const { caseId } = useParams();
  const { fullName, id } = author;
  const { currentUser } = useSelector(state => state.user);
  const displayName = id === currentUser ? "You" : fullName;
  const unknownLabel = getText("common.valueLabels.unknown");
  const authorName = displayName || unknownLabel;
  const authorNode = id ? (
    <Link
      key="author"
      className={classes.authorLink}
      to={getRoute(urls.contributorDetail, { caseId, contributorId: id })}
    >
      {authorName}
    </Link>
  ) : (
    <span key="author" className={classes.authorLink}>
      {authorName}
    </span>
  );

  return (
    <div className={clsx(classes.root, className)} {...props}>
      <div className={classes.box}>
        {subTitle && (
          <Text className={classes.subTitle} type="heading4">
            {subTitle}
          </Text>
        )}
        {!hideUserInfo && (
          <div className={classes.userData}>
            <Avatar className={classes.avatar} src={author.profilePictureURL}>
              {fullName && fullName.charAt(0)}
            </Avatar>
            <Text className={classes.userDataText}>
              {getText("common.valueLabels.byAuthorOnDate", {
                isComponent: true,
                1: authorNode,
                2: date
                  ? format(new Date(date), "dd/MM/yyyy 'at' HH:mm")
                  : unknownLabel
              })}
            </Text>
          </div>
        )}
        <Text className={classes.declaration} key={text} type="contentLabel3">
          {text}
        </Text>
      </div>
    </div>
  );
};

UserAddition.propTypes = {
  noBorder: PropTypes.bool,
  author: PropTypes.shape({
    fullName: PropTypes.string,
    id: PropTypes.string,
    profilePictureURL: PropTypes.string
  }).isRequired,
  className: PropTypes.string,
  date: PropTypes.string.isRequired,
  hideUserInfo: PropTypes.bool,
  subTitle: PropTypes.string,
  text: PropTypes.string
};

UserAddition.defaultProps = {
  noBorder: false,
  className: "",
  hideUserInfo: false,
  subTitle: "",
  text: ""
};

export default UserAddition;
